<template>
  <div>
    <div class="d-flex justify-content-between ts-mb-3">
      <div>
        <h1 class="page-header ts-mb-0">
          {{ $t('preInvoiceV2.pageTitle') }}
        </h1>
      </div>
      <ts-breadcrumb :items="[
        { text: $t('toHomePage'), href: '/' },
        {
          text: $t('preInvoiceV2.pageTitle'),
          active: true
        }
      ]" />
    </div>

    <ts-card>
      <ts-card-wrapper>
        <div class="list-toolbar">
          <div class="toolbar-left">
            <ts-button v-if="canCreate" color="info" outline @click="createNew">
              {{ $t('preInvoiceV2.newPreInvoice') }}
            </ts-button>
          </div>
          <div class="toolbar-right">
            <a-input-search
              v-model="search"
              class="pre-invoice-search"
              :placeholder="$t('preInvoiceV2.searchPreInvoice')"
              @search="fetchData()"
            />
            <a-button class="filter-button" icon="filter" @click="openFilterDrawer">
              {{ $t('preInvoiceV2.filter') }}
            </a-button>
          </div>
        </div>
      </ts-card-wrapper>

      <ts-loading-banner :loading="loading">
        <div class="table-shell">
          <Table
            :columns="columns"
            :data="resources"
            max-height="700"
            highlight-row
            size="small"
            :row-class-name="rowClassName"
          >
            <template slot-scope="{ row }" slot="student">
              <span class="text-strong">{{ studentName(row) || '-' }}</span>
            </template>

            <template slot-scope="{ row }" slot="status">
              <span class="status-pill" :class="isPaid(row) ? 'status-paid' : 'status-unpaid'">
                {{ isPaid(row) ? $t('preInvoiceV2.paid') : $t('preInvoiceV2.unpaid') }}
              </span>
            </template>

            <template slot-scope="{ row }" slot="delete_status">
              <span class="status-pill" :class="isDeleted(row) ? 'status-deleted' : 'status-active'">
                {{ isDeleted(row) ? $t('preInvoiceV2.deleted') : $t('preInvoiceV2.active') }}
              </span>
            </template>

            <template slot-scope="{ row }" slot="total_amount">
              <div class="amount-text">{{ formatAmount(row.total_amount) }}</div>
            </template>

            <template slot-scope="{ row }" slot="amount_due">
              <div class="amount-text">{{ formatAmount(row.amount_due) }}</div>
            </template>

            <template slot-scope="{ row }" slot="created_by">
              <span>{{ row.created_by_name || row.created_by || '-' }}</span>
            </template>

            <template slot-scope="{ row }" slot="branch">
              <span>{{ row.branch_name_en || row.branch_name_kh || '-' }}</span>
            </template>

            <template slot-scope="{ row }" slot="action">
              <div class="action-cell">
                <a v-if="canView" href="#" v-tooltip="$t('preInvoiceV2.viewDetail')" class="action-link action-view" @click.prevent="viewDetail(row)">
                  <i class="fas fa-eye"></i>
                </a>
                <a
                  v-if="canUpdate"
                  href="#"
                  v-tooltip="editTooltip(row)"
                  class="action-link action-edit"
                  :class="{ 'action-disabled': isLocked(row) }"
                  @click.prevent="edit(row)"
                >
                  <i class="fas fa-pen"></i>
                </a>
                <a v-if="canView" href="#" v-tooltip="$t('preInvoiceV2.previewPdf')" class="action-link action-pdf" @click.prevent="previewPdf(row)">
                  <i class="fas fa-file-pdf"></i>
                </a>
                <a
                  v-if="canDelete"
                  href="#"
                  v-tooltip="deleteTooltip(row)"
                  class="action-link action-delete"
                  :class="{ 'action-disabled': isLocked(row) }"
                  @click.prevent="openDeleteModal(row)"
                >
                  <i class="fas fa-circle-notch spin" v-if="deletingId === row.inv_id"></i>
                  <i class="fas fa-trash" v-else></i>
                </a>
              </div>
            </template>

            <template slot="empty">
              <div class="empty-state">
                <strong>{{ emptyStateTitle }}</strong>
                <span>{{ emptyStateHint }}</span>
              </div>
            </template>
          </Table>
        </div>
      </ts-loading-banner>

      <div class="list-pagination">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
    </ts-card>

    <a-drawer
      :visible="filterDrawerVisible"
      :title="$t('preInvoiceV2.filter')"
      width="360"
      placement="right"
      :zIndex="1021"
      @close="closeFilterDrawer"
    >
      <div class="filter-drawer-body">
        <div class="filter-field">
          <label>{{ $t('preInvoiceV2.branch') }}</label>
          <a-select
            v-model="filterForm.branch_ids"
            class="ts-w-full"
            mode="multiple"
            :placeholder="$t('preInvoiceV2.allBranches')"
            allow-clear
          >
            <a-select-option v-for="branch in branches" :key="branch.branch_id" :value="branch.branch_id">
              {{ branch.branch_name_en || branch.branch_name_kh }}
            </a-select-option>
          </a-select>
        </div>
        <div class="filter-field">
          <label>{{ $t('preInvoiceV2.preInvoiceDate') }}</label>
          <a-range-picker
            v-model="filterForm.pre_invoice_date_range"
            class="ts-w-full"
            format="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY"
          />
        </div>
        <div class="filter-field">
          <label>{{ $t('preInvoiceV2.status') }}</label>
          <a-select
            v-model="filterForm.status"
            class="ts-w-full"
            :placeholder="$t('preInvoiceV2.allStatuses')"
            allow-clear
          >
            <a-select-option value="paid">{{ $t('preInvoiceV2.paid') }}</a-select-option>
            <a-select-option value="unpaid">{{ $t('preInvoiceV2.unpaid') }}</a-select-option>
          </a-select>
        </div>
        <div class="filter-field">
          <label>{{ $t('preInvoiceV2.deleteStatus') }}</label>
          <a-select
            v-model="filterForm.delete_status"
            class="ts-w-full"
            :placeholder="$t('preInvoiceV2.allDeleteStatuses')"
            allow-clear
          >
            <a-select-option value="active">{{ $t('preInvoiceV2.active') }}</a-select-option>
            <a-select-option value="deleted">{{ $t('preInvoiceV2.deleted') }}</a-select-option>
          </a-select>
        </div>
      </div>
      <div class="filter-drawer-footer">
        <a-button @click="clearFilter">{{ $t('preInvoiceV2.clearFilter') }}</a-button>
        <a-button type="primary" @click="applyFilter">{{ $t('preInvoiceV2.applyFilter') }}</a-button>
      </div>
    </a-drawer>


    <a-modal
      :visible="deleteModalVisible"
      :title="$t('preInvoiceV2.delete')"
      :ok-text="$t('preInvoiceV2.delete')"
      :cancel-text="$t('preInvoiceV2.cancel')"
      :confirm-loading="Boolean(deletingId)"
      centered
      :zIndex="1022"
      @ok="deleteRecord"
      @cancel="closeDeleteModal"
    >
      <p class="delete-confirm-text">{{ $t('preInvoiceV2.deleteConfirm') }}</p>
      <label class="delete-reason-label">{{ $t('preInvoiceV2.deleteReason') }}</label>
      <a-textarea
        v-model="deleteReason"
        :placeholder="$t('preInvoiceV2.deleteReasonPlaceholder')"
        :auto-size="{ minRows: 3, maxRows: 5 }"
      />
      <div v-if="deleteReasonError" class="delete-reason-error">
        {{ deleteReasonError }}
      </div>
    </a-modal>

    <a-modal
      :visible="detailVisible"
      :title="$t('preInvoiceV2.invoiceDetail')"
      width="860px"
      :footer="null"
      centered
      @cancel="closeDetail"
      :zIndex="1021"
    >
      <ts-loading-banner :loading="detailLoading">
        <div v-if="selectedRecord" class="detail-modal">
          <div class="detail-header">
            <div class="detail-heading">
              <div class="detail-number">{{ selectedRecord.pre_invoice_number || '-' }}</div>
              <div class="detail-meta-line">
                <span>{{ selectedRecord.pre_invoice_date || '-' }}</span>
                <span>{{ selectedRecord.branch_name_en || selectedRecord.branch_name_kh || '-' }}</span>
              </div>
            </div>
            <div class="detail-total">
              <span>{{ $t('preInvoiceV2.totalAmount') }}</span>
              <strong>{{ formatAmount(selectedRecord.total_amount) }}</strong>
              <em :class="isPaid(selectedRecord) ? 'detail-paid' : 'detail-unpaid'">
                {{ isPaid(selectedRecord) ? $t('preInvoiceV2.paid') : $t('preInvoiceV2.unpaid') }}
              </em>
            </div>
          </div>

          <div class="detail-info-grid">
            <div class="detail-info-section">
              <div class="detail-section-title">{{ $t('preInvoiceV2.student') }}</div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.studentName') }}</span>
                <strong>{{ studentName(selectedRecord) || '-' }}</strong>
              </div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.cardNumber') }}</span>
                <strong>{{ selectedRecord.card_number || '-' }}</strong>
              </div>
            </div>
            <div class="detail-info-section">
              <div class="detail-section-title">{{ $t('preInvoiceV2.academicInfo') }}</div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.program') }}</span>
                <strong>{{ selectedRecord.acp_name_foreign || '-' }}</strong>
              </div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.subProgram') }}</span>
                <strong>{{ selectedRecord.acps_name_foreign || '-' }}</strong>
              </div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.academicTerm') }}</span>
                <strong>{{ selectedRecord.term_name || '-' }}</strong>
              </div>
            </div>
            <div class="detail-info-section detail-amount-section">
              <div class="detail-section-title">{{ $t('preInvoiceV2.amount') }}</div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.amountDue') }}</span>
                <strong>{{ formatAmount(selectedRecord.amount_due) }}</strong>
              </div>
              <div class="detail-pair">
                <span>{{ $t('preInvoiceV2.exchangeRate') }}</span>
                <strong>{{ selectedRecord.pre_khr_exchange_rate || '-' }}</strong>
              </div>
            </div>
          </div>

          <div class="detail-audit-section">
            <div class="detail-section-title">{{ $t('preInvoiceV2.auditInfo') }}</div>
            <div class="detail-audit-grid">
              <div class="detail-audit-card">
                <span>{{ $t('preInvoiceV2.createdBy') }}</span>
                <strong>{{ auditUser(selectedRecord.created_by_name, selectedRecord.created_by) }}</strong>
                <em>{{ selectedRecord.created_date || '-' }}</em>
              </div>
              <div v-if="showUpdatedAudit(selectedRecord)" class="detail-audit-card">
                <span>{{ $t('preInvoiceV2.updatedBy') }}</span>
                <strong>{{ auditUser(selectedRecord.modified_by_name, selectedRecord.modified_by) }}</strong>
                <em>{{ selectedRecord.modified_date || '-' }}</em>
              </div>
              <div v-if="isDeleted(selectedRecord)" class="detail-audit-card detail-audit-deleted">
                <span>{{ $t('preInvoiceV2.deletedBy') }}</span>
                <strong>{{ auditUser(selectedRecord.modified_by_name, selectedRecord.modified_by) }}</strong>
                <em>{{ selectedRecord.deleted_date || selectedRecord.modified_date || '-' }}</em>
                <p v-if="selectedRecord.deleted_reason">
                  {{ selectedRecord.deleted_reason }}
                </p>
              </div>
            </div>
          </div>

          <div class="detail-table-header">
            <h4>{{ $t('preInvoiceV2.detailItems') }}</h4>
            <span>{{ (selectedRecord.items || []).length }} {{ $t('preInvoiceV2.itemsSelected') }}</span>
          </div>
          <Table class="detail-items-table" :columns="detailColumns" :data="selectedRecord.items || []" size="small" stripe>
            <template slot-scope="{ row }" slot="fee">
              <div class="ts-leading-tight">
                <div>{{ row.fee_name || row.fee_description || '-' }}</div>
                <div class="ts-text-xs ts-text-gray-500">{{ row.fee_type_name || '-' }}</div>
              </div>
            </template>
            <template slot-scope="{ row }" slot="fee_amount">
              <div class="ts-text-right">{{ formatAmount(row.fee_amount) }}</div>
            </template>
            <template slot-scope="{ row }" slot="discount_amount">
              <div class="ts-text-right">{{ formatAmount(row.discount_amount) }}</div>
            </template>
            <template slot-scope="{ row }" slot="amount_include_vat">
              <div class="ts-text-right ts-font-medium">{{ formatAmount(row.amount_include_vat) }}</div>
            </template>
          </Table>
        </div>
      </ts-loading-banner>
    </a-modal>

    <pdf-preview-modal
      :visible="pdfVisible"
      :title="$t('preInvoiceV2.previewPdf')"
      :src="pdfSrc"
      :loading="pdfLoading"
      :z-index="2000"
      auto-print
      @close="closePdf"
      @update:visible="pdfVisible = $event"
    />
  </div>
</template>


<script>
import notificationAlert from '@/utils/NotificationAlert'
import { mapActions, mapState } from 'vuex'
import PdfPreviewModal from '../components/PdfPreviewModal.vue'

export default {
  name: 'PreInvoiceV2Index',
  components: {
    PdfPreviewModal
  },
  data() {
    return {
      loading: false,
      detailVisible: false,
      detailLoading: false,
      selectedRecord: null,
      deletingId: null,
      deleteModalVisible: false,
      deleteTarget: null,
      deleteReason: '',
      deleteReasonError: null,
      pdfVisible: false,
      pdfLoading: false,
      pdfSrc: '',
      branches: [],
      filterDrawerVisible: false,
      filters: {
        branch_ids: [],
        status: undefined,
        delete_status: undefined,
        pre_invoice_date_range: []
      },
      filterForm: {
        branch_ids: [],
        status: undefined,
        delete_status: undefined,
        pre_invoice_date_range: []
      }
    }
  },
  computed: {
    ...mapState('preInvoiceV2', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.preInvoiceV2.search
      },
      set(value) {
        this.$store.commit('preInvoiceV2/SET_SEARCH', value)
        this.$store.commit('preInvoiceV2/RESET_CURRENT_PAGE')
      }
    },
    hasListCriteria() {
      return Boolean(
        this.$store.state.preInvoiceV2.search ||
        this.filters.status ||
        this.filters.delete_status ||
        this.filters.branch_ids.length ||
        this.filters.pre_invoice_date_range.length
      )
    },
    emptyStateTitle() {
      return this.hasListCriteria
        ? this.$t('preInvoiceV2.noFilteredRecords')
        : this.$t('preInvoiceV2.noRecords')
    },
    emptyStateHint() {
      return this.hasListCriteria
        ? this.$t('preInvoiceV2.noFilteredRecordsHint')
        : this.$t('preInvoiceV2.noRecordsHint')
    },
    canView() {
      return this.can('view-pre-invoices')
    },
    canCreate() {
      return this.can('create-pre-invoices')
    },
    canUpdate() {
      return this.can('update-pre-invoices')
    },
    canDelete() {
      return this.can('delete-pre-invoices')
    },
    canShowActions() {
      return this.canView || this.canUpdate || this.canDelete
    },
    columns() {
      return [
        {
          title: this.$t('preInvoiceV2.branch'),
          slot: 'branch',
          minWidth: 150
        },
        {
          title: this.$t('preInvoiceV2.preInvoiceNumber'),
          key: 'pre_invoice_number',
          minWidth: 155
        },
        {
          title: this.$t('preInvoiceV2.preInvoiceDate'),
          key: 'pre_invoice_date',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.student'),
          slot: 'student',
          minWidth: 150
        },
        {
          title: this.$t('preInvoiceV2.cardNumber'),
          key: 'card_number',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.subProgram'),
          key: 'acps_name_foreign',
          minWidth: 190
        },
        {
          title: this.$t('preInvoiceV2.academicTerm'),
          key: 'term_name',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.grade'),
          key: 'grade_name_foreign',
          width: 135
        },
        {
          title: this.$t('preInvoiceV2.studyTime'),
          key: 'study_time',
          width: 145
        },
        {
          title: this.$t('preInvoiceV2.totalAmount'),
          slot: 'total_amount',
          align: 'right',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.amountDue'),
          slot: 'amount_due',
          align: 'right',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.status'),
          slot: 'status',
          align: 'center',
          width: 95
        },
        {
          title: this.$t('preInvoiceV2.deleteStatus'),
          slot: 'delete_status',
          align: 'center',
          width: 110
        },
        {
          title: this.$t('preInvoiceV2.createdDate'),
          key: 'created_date',
          width: 135
        },
        {
          title: this.$t('preInvoiceV2.createdBy'),
          slot: 'created_by',
          minWidth: 120
        },
        ...(this.canShowActions
          ? [{
              title: this.$t('preInvoiceV2.actions'),
              slot: 'action',
              fixed: 'right',
              align: 'center',
              width: 145
            }]
          : [])
      ]
    },
    detailColumns() {
      return [
        {
          title: this.$t('preInvoiceV2.feeName'),
          slot: 'fee',
          minWidth: 220
        },
        {
          title: this.$t('preInvoiceV2.quantity'),
          key: 'quantity',
          align: 'right',
          width: 90
        },
        {
          title: this.$t('preInvoiceV2.startDate'),
          key: 'fee_start_date',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.endDate'),
          key: 'fee_end_date',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.feeAmount'),
          slot: 'fee_amount',
          align: 'right',
          width: 120
        },
        {
          title: this.$t('preInvoiceV2.discountType'),
          key: 'discount_type',
          align: 'center',
          width: 105
        },
        {
          title: this.$t('preInvoiceV2.discountRate'),
          key: 'discount_rate',
          align: 'right',
          width: 110
        },
        {
          title: this.$t('preInvoiceV2.discountAmount'),
          slot: 'discount_amount',
          align: 'right',
          width: 130
        },
        {
          title: this.$t('preInvoiceV2.amountIncludeVat'),
          slot: 'amount_include_vat',
          align: 'right',
          width: 150
        }
      ]
    }
  },
  async mounted() {
    if (!this.canView) {
      this.$router.replace('/')
      return
    }
    await this.loadBranches()
    this.fetchData()
  },
  beforeDestroy() {
    this.$store.commit('preInvoiceV2/RESET_STATE')
  },
  methods: {
    ...mapActions('preInvoiceV2', {
      fetchResources: 'fetch',
      getBranches: 'getBranches',
      getDetail: 'getDetail',
      previewPdfRequest: 'previewPdf',
      onDelete: 'onDelete'
    }),
    can(permission) {
      return this.$store.getters.authPermissionAllow(permission)
    },
    async fetchData(attributes = {}) {
      this.loading = true
      const params = Object.assign({}, this.filters, attributes)
      return await this.fetchResources(params)
        .catch(error => {
          notificationAlert(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async loadBranches() {
      this.loading = true
      try {
        const response = await this.getBranches()
        this.branches = response.data || []
      } catch (error) {
        notificationAlert(error)
      } finally {
        this.loading = false
      }
    },
    openFilterDrawer() {
      this.filterForm = {
        branch_ids: [...this.filters.branch_ids],
        status: this.filters.status,
        delete_status: this.filters.delete_status,
        pre_invoice_date_range: [...this.filters.pre_invoice_date_range]
      }
      this.filterDrawerVisible = true
    },
    closeFilterDrawer() {
      this.filterDrawerVisible = false
    },
    applyFilter() {
      this.filters = {
        branch_ids: [...this.filterForm.branch_ids],
        status: this.filterForm.status,
        delete_status: this.filterForm.delete_status,
        pre_invoice_date_range: [...this.filterForm.pre_invoice_date_range]
      }
      this.$store.commit('preInvoiceV2/RESET_CURRENT_PAGE')
      this.filterDrawerVisible = false
      this.fetchData()
    },
    clearFilter() {
      this.filterForm = {
        branch_ids: [],
        status: undefined,
        delete_status: undefined,
        pre_invoice_date_range: []
      }
      this.filters = {
        branch_ids: [],
        status: undefined,
        delete_status: undefined,
        pre_invoice_date_range: []
      }
      this.$store.commit('preInvoiceV2/RESET_CURRENT_PAGE')
      this.filterDrawerVisible = false
      this.fetchData()
    },
    createNew() {
      if (!this.canCreate) return
      this.$router.push({ name: 'pre-invoice-v2-create' })
    },
    async viewDetail(row) {
      if (!this.canView) return
      this.detailVisible = true
      this.detailLoading = true
      this.selectedRecord = row
      this.loading = true
      try {
        const response = await this.getDetail(row.inv_id)
        this.selectedRecord = response.data
      } catch (error) {
        notificationAlert(error)
      } finally {
        this.detailLoading = false
        this.loading = false
      }
    },
    closeDetail() {
      this.detailVisible = false
      this.selectedRecord = null
    },
    edit(row) {
      if (!this.canUpdate || this.isLocked(row)) {
        return
      }
      this.$router.push({ name: 'pre-invoice-v2-edit', params: { id: row.inv_id } })
    },
    async previewPdf(row) {
      if (!this.canView) return
      this.pdfVisible = true
      this.pdfLoading = true
      this.pdfSrc = ''
      this.loading = true
      try {
        const response = await this.previewPdfRequest(row.inv_id)
        this.pdfSrc = response.data.url
      } catch (error) {
        this.pdfVisible = false
        notificationAlert(error)
      } finally {
        this.pdfLoading = false
        this.loading = false
      }
    },
    closePdf() {
      this.pdfVisible = false
      this.pdfSrc = ''
    },
    openDeleteModal(row) {
      if (!this.canDelete || this.isLocked(row)) {
        return
      }
      this.deleteTarget = row
      this.deleteReason = ''
      this.deleteReasonError = null
      this.deleteModalVisible = true
    },
    closeDeleteModal() {
      if (this.deletingId) return
      this.deleteModalVisible = false
      this.deleteTarget = null
      this.deleteReason = ''
      this.deleteReasonError = null
    },
    async deleteRecord() {
      if (!this.canDelete || !this.deleteTarget) return
      if (!String(this.deleteReason || '').trim()) {
        this.deleteReasonError = this.$t('preInvoiceV2.deleteReasonRequired')
        return
      }
      this.deletingId = this.deleteTarget.inv_id
      this.loading = true
      try {
        const response = await this.onDelete({
          id: this.deleteTarget.inv_id,
          deleted_reason: this.deleteReason
        })
        notificationAlert(response, 'success')
        this.deleteModalVisible = false
        this.deleteTarget = null
        this.deleteReason = ''
        this.deleteReasonError = null
        await this.fetchData()
      } catch (error) {
        notificationAlert(error)
      } finally {
        this.deletingId = null
        this.loading = false
      }
    },
    isPaid(row) {
      return row.is_paid === true || row.is_paid === 1 || row.is_paid === '1'
    },
    isDeleted(row) {
      return row.is_deleted === true || row.is_deleted === 1 || row.is_deleted === '1'
    },
    showUpdatedAudit(row) {
      return Boolean(row && row.modified_date && !this.isDeleted(row))
    },
    auditUser(name, id) {
      return name || id || '-'
    },
    isLocked(row) {
      return this.isDeleted(row) || this.isPaid(row) || Number(row.amount_paid || 0) > 0
    },
    rowClassName(row) {
      return this.isDeleted(row) ? 'pre-invoice-deleted-row' : ''
    },
    editTooltip(row) {
      if (this.isDeleted(row)) return this.$t('preInvoiceV2.deletedCannotEdit')
      return this.isLocked(row) ? this.$t('preInvoiceV2.paidCannotEdit') : this.$t('preInvoiceV2.edit')
    },
    deleteTooltip(row) {
      if (this.isDeleted(row)) return this.$t('preInvoiceV2.deletedCannotDelete')
      return this.isLocked(row) ? this.$t('preInvoiceV2.paidCannotDelete') : this.$t('preInvoiceV2.delete')
    },
    studentName(row) {
      return row.student_name_en || row.student_name_kh
    },
    formatAmount(value) {
      const amount = Number(value || 0)
      return `$${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`
    }
  }
}
</script>

<style scoped>
.delete-confirm-text {
  margin-bottom: 12px;
  color: #374151;
}

.delete-reason-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #374151;
}

.delete-reason-error {
  margin-top: 6px;
  font-size: 12px;
  color: #dc2626;
}

.action-disabled {
  opacity: 0.42;
  cursor: not-allowed;
  pointer-events: auto;
}

/deep/ .pre-invoice-deleted-row td {
  color: #b91c1c;
  background: #fff1f2 !important;
}

/deep/ .pre-invoice-deleted-row td:not(:last-child) {
  text-decoration: line-through;
}

/deep/ .pre-invoice-deleted-row .status-pill {
  text-decoration: none;
}

.list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  flex: 1;
  justify-content: flex-end;
}

.pre-invoice-search {
  width: 100%;
  max-width: 360px;
}



.filter-button {
  white-space: nowrap;
}

.table-shell {
  margin-top: 12px;
  overflow: auto;
}

.text-strong {
  color: #111827;
  font-weight: 600;
}

.amount-text {
  color: #111827;
  font-weight: 600;
  text-align: right;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 54px;
  padding: 0 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
}

.status-paid {
  background: #dcfce7;
  color: #166534;
}

.status-unpaid {
  background: #fef3c7;
  color: #92400e;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.action-link {
  height: 26px;
  width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.action-link:hover {
  text-decoration: none;
}

.action-view {
  color: #0f766e;
  background: #ccfbf1;
}

.action-edit {
  color: #2563eb;
  background: #dbeafe;
}

.action-pdf {
  color: #b45309;
  background: #fef3c7;
}

.action-delete {
  color: #dc2626;
  background: #fee2e2;
}

.empty-state {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #6b7280;
}

.empty-state strong {
  color: #111827;
  font-size: 15px;
}

.list-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
}

.detail-modal {
  min-height: 220px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
  margin-bottom: 14px;
}

.detail-heading {
  min-width: 0;
}

.detail-number {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.detail-meta-line {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  color: #4b5563;
  font-size: 12px;
}

.detail-meta-line span {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  padding: 3px 8px;
}

.detail-total {
  min-width: 150px;
  text-align: right;
}

.detail-total span {
  color: #6b7280;
  display: block;
  font-size: 12px;
}

.detail-total strong {
  color: #111827;
  display: block;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 2px;
}

.detail-total em {
  display: inline-flex;
  align-items: center;
  height: 22px;
  border-radius: 9999px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  margin-top: 8px;
  padding: 0 9px;
}

.detail-paid {
  background: #dcfce7;
  color: #166534;
}

.detail-unpaid {
  background: #fef3c7;
  color: #92400e;
}

.detail-info-grid {
  display: grid;
  grid-template-columns: 1fr 1.35fr 0.85fr;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-info-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
}

.detail-section-title {
  color: #374151;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
}

.detail-pair {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 8px;
  align-items: baseline;
  margin-top: 6px;
}

.detail-pair span {
  color: #6b7280;
  font-size: 12px;
}

.detail-pair strong {
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  min-width: 0;
}

.detail-audit-section {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
  margin-bottom: 16px;
}

.detail-audit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.detail-audit-card {
  border-left: 3px solid #0ea5e9;
  background: #f8fafc;
  border-radius: 6px;
  padding: 9px 10px;
  min-width: 0;
}

.detail-audit-deleted {
  border-left-color: #dc2626;
  background: #fff1f2;
}

.detail-audit-card span,
.detail-audit-card em {
  display: block;
  color: #6b7280;
  font-size: 12px;
  font-style: normal;
}

.detail-audit-card strong {
  display: block;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  margin-top: 2px;
}

.detail-audit-card p {
  color: #991b1b;
  font-size: 12px;
  margin: 6px 0 0;
  word-break: break-word;
}

.detail-table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-table-header h4 {
  color: #111827;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}

.detail-table-header span {
  color: #6b7280;
  font-size: 12px;
}

.detail-items-table {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

::v-deep .ivu-table td {
  height: 42px;
}

::v-deep .ivu-table-cell {
  overflow: hidden;
  padding-left: 10px;
  padding-right: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .ivu-table th {
  background: #f8fafc;
}

.filter-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 72px;
}

.filter-field label {
  color: #374151;
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.filter-drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
}

@media (max-width: 768px) {
  .list-toolbar,
  .toolbar-left,
  .toolbar-right,
  .list-pagination {
    align-items: stretch;
    flex-direction: column;
  }

  .toolbar-right {
    justify-content: flex-start;
  }

  .pre-invoice-search {
    max-width: none;
  }

  .detail-header,
  .detail-info-grid,
  .detail-audit-grid {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .detail-total {
    text-align: left;
  }

  .detail-pair {
    grid-template-columns: 92px minmax(0, 1fr);
  }
}
</style>
